/**
 *
 * Updates Data On:
 * - Selected Device Change (debounceTime)
 * - Time Range Change
 * - Notification Select
 * - Group Select
 *
 */

import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';

import {AuthService, GoogleAnalyticsService, GroupService} from '../../core';
import {AirComponentType, Group} from '../../shared';
import {DashboardService, EventService, NotificationService} from '../shared';
import {IAwairTimelineListSet, IAwairTimelineSet, ITimePoint, MultiGraphService, TimelineService} from './shared';
import * as fromRoot from '../../reducers';
import * as fromDashboard from '../reducers';
import * as fromAfbApi from '../../afb-api/reducers';

import * as airData from '../../core/store/actions/air-data.action';
import * as avgAirData from '../../core/store/actions/avg-air-data.action';

import {IMyDateRange, IMyDateRangeModel, IMyOptions} from 'mydaterangepicker';
import {combineLatest, forkJoin, Observable, Subject, Subscription} from 'rxjs';


import * as moment from 'moment';
import {AddSelectedDevice, EmptySelectedDevice} from '../actions/dashboard-device.actions';
import {OrganizationWithToken} from '../../core/models/core';
import {filter} from 'rxjs/operators';
import {SelfOrganizationUser} from '../../afb-api/models/organization-user';
import {Device} from '../../afb-api/models/device';
import {Airnote} from '../../afb-api/models/airnote';
import {TriggerEvent} from '../../afb-api/models/trigger-event';
import {NotificationType} from '../../afb-api/enums/notification-type.enum';
import {debounceTime, skip, withLatestFrom} from 'rxjs/internal/operators';

@Component({
  selector: 'awair-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss']
})

export class TimelineComponent implements OnInit, OnDestroy {

  @Output() pinnedNoti: EventEmitter<Airnote | TriggerEvent> = new EventEmitter();
  @Output() removedNoti: EventEmitter<null> = new EventEmitter();

  // Device
  selectedDeviceList: Device[] = [];
  // Date Range
  fromDate: Date;
  toDate: Date;
  // For date picker
  selectedDateRange: IMyDateRange;
  isDatePicker: boolean = false;
  options: IMyOptions;

  // Notifications
  scoreAirNoteListOfDeviceList: Array<Airnote[]>;
  tempAirNoteListOfDeviceList: Array<Airnote[]>;
  humidAirNoteListOfDeviceList: Array<Airnote[]>;
  co2AirNoteListOfDeviceList: Array<Airnote[]>;
  vocAirNoteListOfDeviceList: Array<Airnote[]>;
  dustAirNoteListOfDeviceList: Array<Airnote[]>;
  pm25AirNoteListOfDeviceList: Array<Airnote[]>;

  scoreTriggerEventListOfDeviceList: Array<TriggerEvent[]>;
  tempTriggerEventListOfDeviceList: Array<TriggerEvent[]>;
  humidTriggerEventListOfDeviceList: Array<TriggerEvent[]>;
  co2TriggerEventListOfDeviceList: Array<TriggerEvent[]>;
  vocTriggerEventListOfDeviceList: Array<TriggerEvent[]>;
  dustTriggerEventListOfDeviceList: Array<TriggerEvent[]>;
  pm25TriggerEventListOfDeviceList: Array<TriggerEvent[]>;

  selectedGroup: Group;
  dottedName: string = 'Total Avg.';

  // Enums (to use in template)
  AirComponentType = AirComponentType;

  // Timeline Input Containers
  allTimelineInputContainer: { timelineList: IAwairTimelineListSet, avgTimeline: IAwairTimelineSet };

  // Selected Feed
  selectedFeed: Airnote | TriggerEvent;
  isFeedSelected: boolean = false;
  isClearSelected: boolean = false;

  // Loading method
  loadImmediate: boolean = false;

  hasAwair: boolean;
  hasMint: boolean;
  hasOmni: boolean;

  selectedOrganization$: Observable<OrganizationWithToken> = this.rootStore.pipe(
    select(fromRoot.selectedOrganization),
    filter((selectedOrganization: OrganizationWithToken) => selectedOrganization != null)
  );
  selfOrganizationUser$: Observable<SelfOrganizationUser> = this.afbApiStore.pipe(
    select(fromAfbApi.selfOrganizationUser),
    filter((selfOrganizationUser: SelfOrganizationUser) => selfOrganizationUser != null)
  );

  // Store
  private deviceList$: Observable<Device[]>;
  private selectedDevices$: Observable<Device[]>;
  private selectedGroup$: Observable<Group> = this.rootStore.pipe(
    select(fromRoot.getCoreSelectedGroup)
  );

  private selectedGroupedDeviceSubs: Subscription;
  private airNoteSubs: Subscription;
  private debounceSubs: Subscription;
  private dataDebounce: Subject<{ isDateChanged: boolean, isGroupChanged: boolean }> = new Subject();
  private dataUpdated: Subject<any> = new Subject();

  constructor(private rootStore: Store<fromRoot.State>,
              private afbApiStore: Store<fromAfbApi.State>,
              private dashboardStore: Store<fromDashboard.State>,
              private authService: AuthService,
              private dashboardService: DashboardService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private timelineService: TimelineService,
              private multiGraphService: MultiGraphService,
              private eventService: EventService,
              private notificationService: NotificationService,
              private groupService: GroupService,
              private router: Router) {
  }

  ngOnInit() {
    // Debounce when updating data
    this.debounceSubs = this.dataDebounce.pipe(
      debounceTime(1000),
      withLatestFrom(this.selectedOrganization$)
    ).subscribe(([data, organization]: [{ isDateChanged: boolean, isGroupChanged: boolean }, OrganizationWithToken]) => {
      this.updateData(organization.token, this.selectedDeviceList, this.fromDate, this.toDate);
      this.dataUpdated.next();
    });

    this.options = {
      showSelectDateText: false,
      showApplyBtn: false,
      dateFormat: 'mmm dd, yyyy',
      inline: true,
      disableSince: {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate() + 1
      }
    };
    this.setToday();

    // Air note change subscription
    this.airNoteSubs = this.notificationService.airNoteAnnounced$.pipe(
      withLatestFrom(this.selectedOrganization$)
    ).subscribe(([airNote, organization]: [Airnote, OrganizationWithToken]) => {
      // To let notification component to insert newly created air note to the list before focusing it
      // TODO: Move this logic to "this.ns.airNoteAnnounced$.subscribe" of notification.component
      // since there's no need to announcePinnedNoti when air note is 'edited' and using timeout isn't the best way to do this

      setTimeout(() => {
        this.multiGraphService.announcePinnedNoti(airNote);
      }, 1);
      this.setNotifications(this.selectedDeviceList, this.fromDate, this.toDate, organization.token);
    });

    this.multiGraphService.pinnedNotiAnnounced$.subscribe(
      (noti: Airnote | TriggerEvent) => {
        this.pinnedNoti.emit(noti);
      }
    );
    this.multiGraphService.unpinnedNotiAnnounced$.subscribe(() => {
      this.removedNoti.emit();
    });

    // Ngrx states
    this.deviceList$ = this.afbApiStore.pipe(
      select(fromAfbApi.devices)
    );
    this.selectedDevices$ = this.dashboardStore.pipe(
      select(fromDashboard.selectedDevices)
    );
    this.selectedDevices$.pipe(
      withLatestFrom(this.selectedOrganization$)
    ).subscribe(([selectedDeviceList, organization]: [Device[], OrganizationWithToken]) => {
      this.selectedDeviceList = selectedDeviceList;
      if (this.loadImmediate) {
        this.updateData(organization.token, this.selectedDeviceList, this.fromDate, this.toDate);
      } else {
        // Update Data
        this.dataDebounce.next();
      }
      this.loadImmediate = false;
    });

    this.selectedGroup$.subscribe((selectedGroup: Group) => {
      this.selectedGroup = selectedGroup != null ? {...selectedGroup} : null;
      this.loadImmediate = true;

      if (this.selectedGroup != null) {
        // When selected group,
        this.dottedName = `${this.selectedGroup.name} Avg.`;
        if (this.dottedName[0] != null) {
          this.dottedName = `${this.dottedName.charAt(0).toUpperCase()}${this.dottedName.slice(1)}`;
        }
      } else {
        // When no selected group,
        this.dottedName = 'Total Avg.';
      }
    });
    this.selectedGroupedDeviceSubs = combineLatest(this.deviceList$, this.selectedGroup$,
      (deviceList: Device[], selectedGroup: Group) => {
        const selectedGroupedDevice = this.groupService.getSelectedGroupedDevice(deviceList, selectedGroup);
        this.hasAwair = selectedGroupedDevice.hasAwair;
        this.hasMint = selectedGroupedDevice.hasMint;
        this.hasOmni = selectedGroupedDevice.hasOmni;
      }).subscribe();

    /**
     * Working on Air Data reducer
     */
    this.selfOrganizationUser$.subscribe((organizationUser: SelfOrganizationUser) => {
      combineLatest(
        this.timelineService.getTimelineInputContainer(organizationUser.tempUnit),
        this.timelineService.getAvgTimelineInputContainer(organizationUser.tempUnit),
        this.timelineService.isTimelineLoading(),
        (timelineListInputContainer: IAwairTimelineListSet, avgTimelineInputContainer: IAwairTimelineSet, isLoading: boolean) => {
          return [timelineListInputContainer, avgTimelineInputContainer, isLoading];
        }
      ).subscribe(([timelineListInputContainer, avgTimelineInputContainer, isLoading]:
                     [IAwairTimelineListSet, IAwairTimelineSet, boolean]) => {
        if (!isLoading) {
          // Complete Loading
          this.allTimelineInputContainer = {
            timelineList: timelineListInputContainer,
            avgTimeline: avgTimelineInputContainer
          };
          if (this.router.url === '/dashboard') {
            this.dashboardService.backdropDeviceGrid(false);
            this.dashboardService.backdropTimeline(false);
          }

          if (this.isClearSelected) {
            setTimeout(() => {
              this.unpinTooltip();
            }, 1);
            this.isClearSelected = false;
          } else if (this.isFeedSelected) {
            const deviceIndex: number = this.selectedDeviceList.findIndex((selectedDevice: Device) => {
              return `${selectedDevice.deviceType}_${selectedDevice.deviceId}` ===
                `${this.selectedFeed.deviceType}_${this.selectedFeed.deviceId}`;
            });
            const targetDate: Date = new Date(this.selectedFeed.timestamp);
            const component: AirComponentType = this.selectedFeed.component ?
              this.selectedFeed.component : AirComponentType.Score;

            if (this.selectedFeed.type === NotificationType.Airnote) {
              const airNote: Airnote = <Airnote>this.selectedFeed;

              // Setting timeout to wait for timelinelist in timeline-graph to update before tooltip is hovered
              this.multiGraphService.announceHover(targetDate);
              this.multiGraphService.announceClick(true, component);
              const hoveredIndex: number = timelineListInputContainer[component][deviceIndex]
                .map((timePoint: ITimePoint) => new Date(timePoint.x).getTime()).indexOf(new Date(airNote.timestamp).getTime());
              this.multiGraphService.announceIndicatorDotHover(hoveredIndex, deviceIndex);
              // Passing airNote to let user to edit right away
              this.multiGraphService.announcePinTooltip(hoveredIndex, component, deviceIndex, true);
            } else {
              const triggerEvent: TriggerEvent = <TriggerEvent>this.selectedFeed;

              // Setting timeout to wait for timelinelist in timeline-graph to update before tooltip is hovered
              this.multiGraphService.announcePinTe(triggerEvent, component, deviceIndex);
            }
            // Scroll to component
            const element: HTMLElement = document.getElementById(component + '-timeline');
            document.documentElement.scrollTop = element.offsetTop;

            this.isFeedSelected = false;
          }

        } else {
          // Loading
          if (this.router.url === '/dashboard') {
            // Making z-index 600 since selected coreDevice item has z-index of 550
            this.dashboardService.backdropTimeline(true, '#F8F9FA', 0.5, 600);
            this.dashboardService.backdropDeviceGrid(true, '#F8F9FA', 0.5, 600);
          }
        }
      })
    });
  }

  ngOnDestroy() {
    if (this.airNoteSubs) {
      this.airNoteSubs.unsubscribe();
    }
    if (this.debounceSubs) {
      this.debounceSubs.unsubscribe();
    }
    if (this.selectedGroupedDeviceSubs) {
      this.selectedGroupedDeviceSubs.unsubscribe();
    }
    // Temporary fix for devices being selected after navigating main menu
    this.dashboardStore.dispatch(new EmptySelectedDevice());
  }

  /**
   * DatePicker events
   */
  onDateRangeChanged(event: IMyDateRangeModel, organizationToken: string): void {
    if (event.formatted) {
      if (this.fromDate.getTime() === event.beginJsDate.getTime()
        && this.toDate.getTime() === event.endJsDate.getTime()) {

        this.isDatePicker = false;
      } else {
        // For View
        this.selectedDateRange = {beginDate: event.beginDate, endDate: event.endDate};
        // For real inputs
        this.fromDate = new Date(event.beginJsDate);
        this.toDate = new Date(event.endJsDate);
        this.fromDate.setHours(0, 0, 0, 0);
        this.toDate.setHours(23, 59, 59, 59);

        // Close date range picker
        this.isDatePicker = false;

        this.googleAnalyticsService.emitEvent('Timeline', 'adjust', 'Time Range', moment(this.toDate).diff(moment(this.fromDate), 'days'));
        // No debounceTime here to load immediately
        this.updateData(organizationToken, this.selectedDeviceList, this.fromDate, this.toDate);
      }
    }
  }

  onDateRangePicker(event: MouseEvent, dateContainer: HTMLElement): void {
    const dateInputEl: Element = dateContainer.querySelector('.time-range');
    const daterangepickerEl: Element = dateContainer.querySelector('#daterangepicker');

    if (!(dateContainer.contains(event.target as HTMLElement)) && dateInputEl) {
      // If selected element is not datepicker element,
      this.isDatePicker = false;

    } else if (daterangepickerEl != null && daterangepickerEl.contains(event.target as HTMLElement)) {
      // If datepicker is opened and selected element is itself
      this.isDatePicker = true;
    } else {
      // If selected element is datepicker element, make it toggle
      // On init, element's display is set to ''
      this.isDatePicker = !this.isDatePicker;
    }
  }

  /**
   * Notification methods
   */
  setNotifications(selectedDeviceList: Device[], fromDate: Date, toDate: Date, organizationToken: string): void {
    const triggerEventObs = [];
    const airNoteObs = [];

    for (let i = 0; i < selectedDeviceList.length; i++) {
      triggerEventObs.push(this.notificationService.requestTriggerEvent(
        organizationToken,
        selectedDeviceList[i].deviceType,
        selectedDeviceList[i].deviceId,
        'timestamp',
        undefined,
        fromDate,
        toDate)
      );
      airNoteObs.push(this.notificationService.requestAirNote(
        organizationToken,
        selectedDeviceList[i].deviceType,
        selectedDeviceList[i].deviceId,
        'timestamp',
        undefined,
        fromDate,
        toDate)
      );
    }

    const notificationObs = [forkJoin(triggerEventObs), forkJoin(airNoteObs)];

    forkJoin(notificationObs).pipe(
      withLatestFrom(this.selfOrganizationUser$)
    ).subscribe(
      ([notification, selfOrganizationUser]: [[Array<TriggerEvent[]>, Array<Airnote[]>], SelfOrganizationUser]) => {
        // Initialize
        const tempAirNoteSetList = [];
        const tempTriggerEventSetList = [];

        const triggerEventListOfDeviceList: Array<TriggerEvent[]> = notification[0];
        const airNoteListOfDeviceList: Array<Airnote[]> = notification[1];

        for (let i = 0; i < triggerEventListOfDeviceList.length; i++) {
          tempTriggerEventSetList.push(this.notificationService.triggerEventListToSet(
            triggerEventListOfDeviceList[i], selfOrganizationUser.tempUnit));
        }
        for (let i = 0; i < airNoteListOfDeviceList.length; i++) {
          tempAirNoteSetList.push(this.notificationService.airNoteListToSet(
            airNoteListOfDeviceList[i], selfOrganizationUser.tempUnit));
        }

        this.scoreAirNoteListOfDeviceList = this.notiSetToList(tempAirNoteSetList, 'score');
        this.tempAirNoteListOfDeviceList = this.notiSetToList(tempAirNoteSetList, 'temp');
        this.humidAirNoteListOfDeviceList = this.notiSetToList(tempAirNoteSetList, 'humid');
        this.co2AirNoteListOfDeviceList = this.notiSetToList(tempAirNoteSetList, 'co2');
        this.vocAirNoteListOfDeviceList = this.notiSetToList(tempAirNoteSetList, 'voc');
        this.dustAirNoteListOfDeviceList = this.notiSetToList(tempAirNoteSetList, 'dust');
        this.pm25AirNoteListOfDeviceList = this.notiSetToList(tempAirNoteSetList, 'pm25');

        this.scoreTriggerEventListOfDeviceList = this.notiSetToList(tempTriggerEventSetList, 'score');
        this.tempTriggerEventListOfDeviceList = this.notiSetToList(tempTriggerEventSetList, 'temp');
        this.humidTriggerEventListOfDeviceList = this.notiSetToList(tempTriggerEventSetList, 'humid');
        this.co2TriggerEventListOfDeviceList = this.notiSetToList(tempTriggerEventSetList, 'co2');
        this.vocTriggerEventListOfDeviceList = this.notiSetToList(tempTriggerEventSetList, 'voc');
        this.dustTriggerEventListOfDeviceList = this.notiSetToList(tempTriggerEventSetList, 'dust');
        this.pm25TriggerEventListOfDeviceList = this.notiSetToList(tempTriggerEventSetList, 'pm25');
      },
      (error: Response) => {
        alert('Failed to retrieve notification data.\nPlease try again later.');
      }
    );
  }

  removeNotifications(): void {
    this.scoreAirNoteListOfDeviceList = [];
    this.tempAirNoteListOfDeviceList = [];
    this.humidAirNoteListOfDeviceList = [];
    this.co2AirNoteListOfDeviceList = [];
    this.vocAirNoteListOfDeviceList = [];
    this.dustAirNoteListOfDeviceList = [];
    this.pm25AirNoteListOfDeviceList = [];

    this.scoreTriggerEventListOfDeviceList = [];
    this.tempTriggerEventListOfDeviceList = [];
    this.humidTriggerEventListOfDeviceList = [];
    this.co2TriggerEventListOfDeviceList = [];
    this.vocTriggerEventListOfDeviceList = [];
    this.dustTriggerEventListOfDeviceList = [];
    this.pm25TriggerEventListOfDeviceList = [];
  }

  invokePinOnNotification(selectedDevice: Device, notification: Airnote | TriggerEvent, organizationToken: string): void {
    const notiDate: Date = new Date(notification.timestamp);
    const notiFromDate: Date = new Date(notiDate);
    const notiToDate: Date = new Date(notiDate);
    notiFromDate.setHours(0, 0, 0, 0);
    notiToDate.setHours(23, 59, 59, 0);

    this.fromDate = notiFromDate;
    this.toDate = notiToDate;
    this.selectedDateRange = {
      beginDate: {year: this.fromDate.getFullYear(), month: this.fromDate.getMonth() + 1, day: this.fromDate.getDate()},
      endDate: {year: this.toDate.getFullYear(), month: this.toDate.getMonth() + 1, day: this.toDate.getDate()}
    };

    this.isFeedSelected = true;
    this.selectedFeed = Object.assign({}, notification);

    // If there's no selected device, select the device of notification
    if (this.selectedDeviceList.length === 0) {
      this.selectedDeviceList = [selectedDevice];
      this.loadImmediate = true;
      const selectedDeviceUuid: string = `${selectedDevice.deviceType}_${selectedDevice.deviceId}`;
      this.dashboardStore.dispatch(new AddSelectedDevice({
        deviceUuid: selectedDeviceUuid
      }));
    } else {
      this.updateData(organizationToken, this.selectedDeviceList, this.fromDate, this.toDate);
    }
  }

  unpinTooltip(): void {
    this.multiGraphService.announceUnpinTooltip();
    this.multiGraphService.announceIndicatorDotOut();
    this.multiGraphService.announceOut();

    this.multiGraphService.announceRemoveTe();
  }

  backToInitState(): void {
    this.isClearSelected = true;
    this.loadImmediate = true;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  /**
   * Private methods
   */
  private updateData(organizationToken: string,
                     selectedDeviceList: Device[],
                     fromDate: Date,
                     toDate: Date): void {

    // This is to prevent misleading behavior when previous toggle isn't off yet and selected another notification toggle
    this.unpinTooltip();

    // Remove pinned indicator if data is being updated
    if (this.multiGraphService.isPinned) {
      this.multiGraphService.announceClick(false, null);
      this.multiGraphService.announceOut();
    }

    if (selectedDeviceList[0] == null || selectedDeviceList.length === 0) {
      // If selectedDeviceList is empty, remove all noti, orgTimelineSet(dottedLine), and set orgAvg to timelineListSet

      // Notifications (air-note and sensor noti) should not show up in org view
      this.removeNotifications();
    } else {
      // If 1 or more device/s are selected, update noti, orgTimelineSet, and timelineListSet
      this.setNotifications(selectedDeviceList, fromDate, toDate, organizationToken);
    }

    this.rootStore.dispatch(new airData.UpdateAirDataAction({
      orgToken: organizationToken,
      interval: 15,
      selectedDeviceList: selectedDeviceList,
      fromDate: fromDate,
      toDate: toDate
    }));

    this.rootStore.dispatch(new avgAirData.UpdateAvgAction({
      orgToken: organizationToken,
      groupId: this.selectedGroup != null ? this.selectedGroup.id : null,
      interval: 15,
      fromDate: fromDate,
      toDate: toDate
    }));
  }

  private notiSetToList(notiSetList: any[], type: string) {
    if (notiSetList && notiSetList.length > 0) {
      const notiTypeList: any[] = [];

      for (let i = 0; i < notiSetList.length; i++) {
        notiTypeList.push(notiSetList[i][type]);
      }

      return notiTypeList;
    }
  }

  private setToday(): void {
    this.fromDate = new Date();
    this.toDate = new Date();
    this.fromDate.setHours(0, 0, 0, 0);
    this.toDate.setHours(23, 59, 59, 59);

    this.selectedDateRange = {
      beginDate: {year: this.fromDate.getFullYear(), month: this.fromDate.getMonth() + 1, day: this.fromDate.getDate()},
      endDate: {year: this.toDate.getFullYear(), month: this.toDate.getMonth() + 1, day: this.toDate.getDate()},
    };
  }
}
